import { computed } from "vue";
import { useCollection, useCurrentUser, useDocument } from "vuefire";
import {
  collectionGroup,
  doc,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import type {
  Experiment,
  Method,
  Role,
  TeamInvite,
  WorkspaceInvite,
} from "./model";

export type BrieflyDoc =
  | (Method & { type: "method" })
  | (Experiment & { type: "experiment" });

export type UserProfile = {
  username: string;
  homeWorkspace: string;
  otherWorkspaces?: Record<string, { role: Role }>;
};

export const useUserProfile = () => {
  const user = useCurrentUser();
  const userProfileRef = computed(() =>
    user.value?.uid ? doc(db, `users/${user.value.uid}`) : null
  );
  const userProfile = useDocument<UserProfile>(userProfileRef);
  const userDisplayName = computed(
    () =>
      userProfile.value?.homeWorkspace ??
      user.value?.displayName ??
      user.value?.email ??
      "Unknown"
  );

  const userInvites = useCollection<WorkspaceInvite>(
    computed(() =>
      user.value
        ? query(
            collectionGroup(db, "invites"),
            where("emailId", "==", user.value.email),
            orderBy("workspaceId", "asc")
          )
        : null
    ),
    { ssrKey: "invites" }
  );

  // Team invites — same collection group as workspace invites, but scoped
  // to team invite docs via `orderBy("teamId")` (Firestore's implicit
  // "field must exist" behavior filters out workspace invite docs, which
  // don't have a teamId field).
  const userTeamInvites = useCollection<TeamInvite>(
    computed(() =>
      user.value
        ? query(
            collectionGroup(db, "invites"),
            where("emailId", "==", user.value.email),
            orderBy("teamId", "asc")
          )
        : null
    ),
    { ssrKey: "team-invites" }
  );

  const username = computed(() => userProfile.value?.username);

  return {
    user,
    userProfileRef,
    userProfile,
    userInvites,
    userTeamInvites,
    userDisplayName,
    username,
  };
};
